import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

// All specializations in the SAME order as the /servizi page (S.01 → S.09),
// with Risanamento umidità di risalita as sub-specializzazione in coda.
const SPECIALIZATIONS = [
  "Ristrutturazioni chiavi in mano",       // S.01
  "Impermeabilizzazione Poliurea",         // S.02
  "Coibentazione Poliuretano",             // S.03
  "Efficientamento Energetico",            // S.04
  "Pareti Ventilate",                      // S.05
  "Rivestimenti Piscine",                  // S.06
  "Rivestimenti Protettivi",               // S.07
  "Restauro Conservativo",                 // S.08
  "Finiture e Rivestimenti",               // S.09
  "Risanamento umidità di risalita",       // sotto-specializzazione di S.09
];

// Bottom target sectors — alternating white / orange colour pattern.
const SECTORS = [
  { label: "Residenziale", accent: true },
  { label: "Condominiale", accent: false },
  { label: "Ospitalità", accent: true },
  { label: "Retail & Uffici", accent: false },
  { label: "Industriale", accent: true },
];

/**
 * IntroExperience — cinematic first-impression overlay.
 * Designed to fit ONE viewport on both desktop and mobile.
 */
export default function IntroExperience() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.pathname !== "/") return;
    setOpen(true);
    document.documentElement.style.overflow = "hidden";
    return () => { document.documentElement.style.overflow = ""; };
  }, []);

  const dismiss = () => {
    setOpen(false);
    document.documentElement.style.overflow = "";
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="ghs-intro"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, y: -20, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }}
          transition={{ duration: 0.4 }}
          data-testid="intro-experience"
          className="fixed inset-0 z-[999] bg-foreground text-background overflow-hidden isolate flex flex-col"
        >
          {/* Grid backdrop */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.06] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)",
              backgroundSize: "60px 60px",
              color: "#ffffff",
            }}
          />
          {/* Ambient orange glows */}
          <motion.div
            aria-hidden
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 0.35, scale: 1 }}
            transition={{ duration: 1.4, ease: "easeOut" }}
            className="absolute -right-40 -top-40 w-[720px] h-[720px] rounded-full bg-primary blur-[140px] pointer-events-none"
          />
          <motion.div
            aria-hidden
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 0.2, scale: 1 }}
            transition={{ duration: 1.8, delay: 0.4, ease: "easeOut" }}
            className="absolute -left-40 -bottom-40 w-[520px] h-[520px] rounded-full bg-primary blur-[140px] pointer-events-none"
          />
          {/* Slow orbital scan-line */}
          <motion.div
            aria-hidden
            initial={{ y: "-100%" }}
            animate={{ y: "220%" }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear", repeatDelay: 3 }}
            className="absolute inset-x-0 h-[1px] bg-primary/60 pointer-events-none blur-[1px]"
          />

          {/* Corner brand */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="absolute top-4 left-5 md:top-6 md:left-10 flex items-center gap-2 z-10"
          >
            <span className="w-3 h-3 bg-primary" />
            <span className="font-mono text-[10px] md:text-xs uppercase tracking-widest text-background/70">
              GREENHOUSE SOLUTIONS<span className="text-primary">.</span> SpA
            </span>
          </motion.div>

          {/* Main content — takes all remaining space, vertically centred */}
          <div className="relative flex-1 min-h-0 w-full flex flex-col justify-center px-5 md:px-10 lg:px-16 pt-14 pb-6 md:pt-16 md:pb-10 max-w-[1400px] mx-auto">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              data-testid="intro-eyebrow"
              className="font-mono text-[10px] md:text-xs uppercase tracking-widest text-primary mb-3 md:mb-5"
            >
              // Benvenuto — dal 2022 ad oggi
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-row items-center gap-4 sm:gap-6 md:gap-10"
            >
              <h1
                className="font-head font-black text-[32px] leading-[0.95] sm:text-5xl sm:leading-[0.9] md:text-6xl md:leading-[0.9] lg:text-[92px] lg:leading-[0.88] tracking-tightest min-w-0 flex-1 [@media(max-height:700px)]:text-[26px] [@media(max-height:700px)]:leading-[1]"
              >
                Costruiamo<br />
                con <span className="text-primary">visione</span>.
              </h1>
              <img
                src="/images/ghs-logo-full-white.png?v=2"
                alt="GHS SpA — Greenhouse Solutions SPA"
                width="636"
                height="340"
                data-testid="intro-logo"
                className="w-24 min-[400px]:w-28 sm:w-40 md:w-80 lg:w-96 h-auto shrink-0 [@media(max-height:700px)]:w-44"
                loading="eager"
              />
            </motion.div>

            {/* Emotional broad-target paragraph — tight */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.7 }}
              className="mt-4 md:mt-6 max-w-3xl text-sm md:text-base lg:text-lg text-background/80 leading-relaxed [@media(max-height:700px)]:text-[12px] [@media(max-height:700px)]:mt-2 [@media(max-height:700px)]:leading-snug"
            >
              Ogni edificio ha una storia — e un futuro da proteggere.
              Casa di famiglia, condominio, hotel sul mare o capannone industriale:
              trasformiamo <span className="text-background">immobili complessi</span> in luoghi
              <br />
              <span className="text-primary whitespace-nowrap">duraturi, efficienti e di qualità</span>.
            </motion.p>

            {/* Uniform specializations — single line, wraps naturally */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.7 }}
              data-testid="intro-specializations"
              className="mt-4 md:mt-6 max-w-4xl [@media(max-height:700px)]:mt-2"
            >
              <p className="font-mono text-[10px] md:text-[11px] uppercase tracking-widest text-background/40 mb-2 [@media(max-height:700px)]:mb-1">
                // Le nostre specializzazioni
              </p>
              <div className="flex flex-wrap gap-x-2.5 gap-y-1 text-[13px] md:text-sm text-background/85 [@media(max-height:700px)]:text-[11px] [@media(max-height:700px)]:gap-x-2">
                {SPECIALIZATIONS.map((s, i) => (
                  <span key={s} data-testid={`intro-spec-${i}`} className="inline-flex items-center gap-2.5">
                    <span>{s}</span>
                    {i < SPECIALIZATIONS.length - 1 && <span className="text-primary/70">·</span>}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Curiosity / tech tease — compact — hidden on very short viewports */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.55, duration: 0.7 }}
              className="mt-4 md:mt-6 border-l-2 border-primary pl-3 md:pl-5 max-w-2xl [@media(max-height:640px)]:hidden [@media(max-height:700px)]:mt-2"
            >
              <p className="font-mono text-[10px] md:text-[11px] uppercase tracking-widest text-primary/90 mb-1.5">
                // Verso il futuro
              </p>
              <p className="text-[13px] md:text-sm lg:text-base text-background/85 leading-relaxed [@media(max-height:700px)]:text-[11px] [@media(max-height:700px)]:leading-snug">
                Portiamo in cantiere la <span className="text-background">poliurea di nuova generazione</span> e materiali ad alte prestazioni,
                testati per resistere alle condizioni più estreme — protezioni che nel
                <span className="text-primary"> settore edilizio</span> stanno riscrivendo le regole del gioco.
              </p>
            </motion.div>

            {/* Single CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.85, duration: 0.7 }}
              className="mt-6 md:mt-8 [@media(max-height:700px)]:mt-4"
            >
              <button
                type="button"
                onClick={dismiss}
                data-testid="intro-enter-btn"
                className="group inline-flex items-center gap-3 bg-primary text-primary-foreground font-mono text-xs md:text-sm uppercase tracking-widest px-6 md:px-8 py-3.5 md:py-4 md:hover:bg-background md:hover:text-foreground transition-colors [@media(max-height:700px)]:py-2.5 [@media(max-height:700px)]:px-5"
              >
                <span>Entra nel sito</span>
                <span className="w-6 h-[1px] bg-current transition-all duration-300 md:group-hover:w-10" />
                <ArrowUpRight className="w-4 h-4 md:w-5 md:h-5 transition-transform duration-300 md:group-hover:-translate-y-0.5 md:group-hover:translate-x-0.5" />
              </button>
            </motion.div>
          </div>

          {/* Sectors row — flex child at bottom, transparent (no dark bar), always visible */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.05, duration: 0.8 }}
            data-testid="intro-sectors"
            className="relative shrink-0 z-[5] pb-4 md:pb-6 pt-2 md:pt-2 [@media(max-height:700px)]:pb-3 [@media(max-height:700px)]:pt-1"
          >
            <div className="px-5 md:px-10 lg:px-16 max-w-[1400px] mx-auto">
              <div className="flex items-center justify-center md:justify-between gap-x-3 gap-y-1 flex-wrap font-mono text-[9px] md:text-[11px] uppercase tracking-widest">
                {SECTORS.map((s, i) => (
                  <span
                    key={s.label}
                    data-testid={`intro-sector-${i}`}
                    className={`inline-flex items-center gap-1.5 ${s.accent ? "text-primary" : "text-background/70"}`}
                  >
                    <span className={`w-1 h-1 ${s.accent ? "bg-primary" : "bg-background/60"} rounded-full`} />
                    {s.label}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
